import type * as React from "react";
import { useMemo, useState } from "react";
import { makeStyles } from "@mui/styles";
import { C_ON_SURFACE } from "../icon/Colors";
import { Select, type SelectProps } from "./Select";
import { MaterialInput } from "../materialInput/MaterialInput";
import { Icon } from "../icon/Icon";
import { IconSize } from "../icon/IconSize";
import { IconType } from "../icon/IconType";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";
import { SimpleSelectItem } from "~/common/components/Select/SimpleSelect";

export interface SimpleSelectProps extends Omit<SelectProps, "multiple"> {
  values: DropdownEntry<string | number>[];
  showFilter?: boolean;
  placeholder?: string;
}

const useSearchBoxStyle = makeStyles({
  root: {
    padding: "10px 20px",
    minHeight: "50px",
    color: C_ON_SURFACE,
  },
});

export const SimpleSelectWithStar = (props: SimpleSelectProps) => {
  const { values, children, placeholder, showFilter = false, ...rest } = props;

  const { t } = useTranslation();
  const [filterText, setFilterText] = useState("");
  const elements = useMemo(() => {
    let filteredValues = values;
    if (filterText) {
      filteredValues = values.filter((element) => element.stringLabel?.includes(filterText));
    }
    return createSingleSelectItems(filteredValues, rest["data-test-id"]);
  }, [values, filterText]);

  const sbClasses = useSearchBoxStyle();

  return (
    <Select
      {...rest}
      renderValue={(value) => values.find((item) => item.value === value)?.label ?? undefined}
      variant={"outlined"}
    >
      {showFilter && (
        <div className={sbClasses.root}>
          <MaterialInput
            placeholder={t("generic.searchHere")}
            onChange={(e) => setFilterText(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            value={filterText}
            onClick={PREVENT_CLOSE}
          />
        </div>
      )}
      {placeholder && (
        <SimpleSelectItem value={""} data-test-id={rest["data-test-id"] + "-placeholder"}>
          {placeholder}
        </SimpleSelectItem>
      )}
      {elements}
      {children}
    </Select>
  );
};

export const createSingleSelectItems = (items: DropdownEntry<string | number>[], testId?: string) =>
  items.map((item) => {
    const childId = testId ? `${testId}-item` : undefined;
    return (
      <SimpleSelectItem key={item.value} value={item.value} data-test-id={childId}>
        <>
          {item.defaultValue ? (
            <Icon className={"mr-1"} type={IconType.Star} size={IconSize.FONT_10} />
          ) : (
            <div style={{ width: 15 }} />
          )}
          {item.label}
        </>
      </SimpleSelectItem>
    );
  });

const PREVENT_CLOSE = (e: React.MouseEvent) => e.stopPropagation();
